// CSV Parser for Proposal Builder Timber Import
// Handles Materials CSV from design software
// Format: [Line#], [production#], piece name, group, material, quantity, height("), width("), real length, order length

import type { TimberListItem, ProposalNodeSet } from '../types';
import { isTimberGradeSuffixCell, parseTimberMaterialColumn } from './timberMaterialParser';
import { createDefaultNodeSet } from '../types';
import { isCadworkSteelCsv } from './proposalHardwareCsvParser';

/** Default species/grade applied to imported timber lines (for materials DB matching & RFQ). */
export const DEFAULT_PROPOSAL_TIMBER_SPECIES = 'Eastern White Pine';
export const DEFAULT_PROPOSAL_TIMBER_GRADE = '#2';

/** Absent or false rounds height and width to the nearest inch. True keeps the CSV number. */
export interface TimberCsvParseOptions {
    trueDimensions?: boolean;
}

/**
 * Cross-section inches for quotes, tables, and board feet.
 * True dimensions keep 7.5, 8, and 9.25. Otherwise 7.5 becomes 8.
 * Length is never passed through here.
 */
export function formatTimberCrossSectionInches(inches: number, trueDimensions?: boolean): number {
    if (!Number.isFinite(inches)) return 0;
    if (trueDimensions) return Math.round(inches * 10000) / 10000;
    return Math.round(inches);
}

function crossSectionFields(
    rawHeight: number,
    rawWidth: number,
    orderLength: number,
    trueDimensions?: boolean,
): Pick<ParsedTimberItem, 'height' | 'width' | 'normalizedDimensions' | 'boardFeet' | 'trueDimensions'> {
    const height = formatTimberCrossSectionInches(Math.min(rawHeight, rawWidth), trueDimensions);
    const width = formatTimberCrossSectionInches(Math.max(rawHeight, rawWidth), trueDimensions);
    return {
        height,
        width,
        normalizedDimensions: `${height}x${width}x${orderLength}`,
        boardFeet: Math.round(((height * width * orderLength) / 12) * 100) / 100,
        ...(trueDimensions ? { trueDimensions: true } : {}),
    };
}

export interface ParsedTimberItem {
    pieceName: string;
    group: string;
    material: string;
    quantity: number;
    height: number; // in inches
    width: number; // in inches
    orderLength: number; // in feet
    // Normalized dimensions (smaller first for h×w)
    normalizedDimensions: string; // e.g., "4x8x12"
    boardFeet: number;
    /** App-assigned sequential line number (1..N in data row order). */
    lineNumber: number;
    /** Cadwork production# when present in source CSV. */
    productionNumber?: string;
    /** LOD200 Storey / Level tag when present (e.g. `2.00` or `2.00 2.01`). */
    storey?: string;
    /** Source CSV keeps decimal cross-section inches. */
    trueDimensions?: boolean;
}

export interface CSVParseResult {
    items: ParsedTimberItem[];
    groups: string[];
    csvType: 'materials' | 'production';
    totalItems: number;
    errors: string[];
    debugInfo: {
        totalLines: number;
        dataLines: number;
        delimiter: string;
        sampleRow: string[];
    };
}

export interface TimberCsvColumnLayout {
    delimiter: string;
    hasLineNumberCol: boolean;
    hasProductionCol: boolean;
    /** Cadwork export with Species + Grade in separate columns after Material. */
    hasSeparateGradeCol: boolean;
    nameIdx: number;
    groupIdx: number;
    materialIdx: number;
    gradeIdx: number;
    qtyIdx: number;
    heightIdx: number;
    widthIdx: number;
    orderLenIdx: number;
    productionIdx: number;
}

const METADATA_ROW_COUNT = 3;

/** True when CSV has Cadwork metadata header (3D file / Date / Designer). */
export function isCadworkTimberCsv(csvText: string): boolean {
    const lines = csvText.replace(/^\uFEFF/, '').trim().split(/\r?\n/);
    for (let i = 0; i < Math.min(8, lines.length); i++) {
        if (lines[i]?.toLowerCase().includes('3d file')) return true;
    }
    return false;
}

/**
 * Cadwork LOD100 export: same 3-line metadata header as the classic tally, but the
 * table is `line#, [ROOM,] Sub group, Name, INT or EXT, Material, [GRADE,] Quantity,
 * Width, Height, Length Real, Length Rough` with an embedded header row + `[inch]`
 * units row, and ALL dimensions (including lengths) in inches.
 *
 * Header variants:
 *   - multi-structure ROOM: includes ROOM (one CRM structure per room)
 *   - multi-structure Building (LOD200): Building instead of ROOM, no line-number column
 *   - LOD200 Storey: optional Storey / Story / Level between Building and Sub group
 *   - LOD200.6: optional No. Production + BDFT (ROUGH) / BDFT (REAL) totals; SUM= footer
 *   - LOD200 imperial: same columns, but H/W/lengths are `6"` / `13'-5"` / `14'`
 *     (the units row still says [inch])
 *   - single-structure: no ROOM / Building, optional GRADE (one CRM structure for the file)
 */
export function isLod100TimberCsv(csvText: string): boolean {
    if (isCadworkSteelCsv(csvText)) return false;
    return splitLod100Lines(csvText) != null || lod100HasInchUnitsRow(csvText);
}

/** Column indices for one LOD100 row, derived from the embedded header row. */
interface Lod100ColumnMap {
    /** -1 when the export has no ROOM / Building column (single-structure list). */
    roomIdx: number;
    /** Preview label for the grouping column; null when there is no group column. */
    groupColumnLabel: 'Room' | 'Building' | null;
    /** -1 when the export has no Storey / Story / Level column. */
    storeyIdx: number;
    subGroupIdx: number;
    nameIdx: number;
    intExtIdx: number;
    materialIdx: number;
    /** -1 when grade is not a dedicated column. */
    gradeIdx: number;
    qtyIdx: number;
    widthIdx: number;
    heightIdx: number;
    realLenIdx: number;
    roughLenIdx: number;
    /** -1 when the export has no No. Production column. */
    productionIdx: number;
    /** -1 when Cadwork did not print BDFT (ROUGH). */
    bdftRoughIdx: number;
    /** -1 when Cadwork did not print BDFT (REAL). */
    bdftRealIdx: number;
}

/** True when cells are the embedded LOD100 header (Sub group + Name; ROOM / INT optional). */
function isLod100HeaderCells(cells: string[]): boolean {
    const lower = cells.map((c) => (c || '').trim().toLowerCase());
    const hasSubGroup = lower.some((h) => h === 'sub group' || h === 'subgroup' || h === 'sub-group');
    const hasName = lower.includes('name');
    const hasIntExt = lower.some((h) => h === 'int or ext' || h.startsWith('int'));
    const hasRoom = lower.includes('room');
    const hasBuilding = lower.includes('building');
    const hasLod100Length = lower.some((h) => h.startsWith('length rough') || h.startsWith('length real'));
    return hasSubGroup && hasName && (hasIntExt || hasRoom || hasBuilding || hasLod100Length);
}

function lod100HasInchUnitsRow(csvText: string): boolean {
    const lines = csvText.replace(/^\uFEFF/, '').trim().split(/\r?\n/);
    return lines.some((line) => (line || '').toLowerCase().includes('[inch]'));
}

/** True when preview cells are the LOD100 units row ([inch] markers). */
function isLod100UnitsCells(cells: string[]): boolean {
    return cells.some((c) => (c || '').toLowerCase().includes('[inch]'));
}

function lod100ColumnMapFromHeader(headerCells: string[]): Lod100ColumnMap {
    const lower = headerCells.map((c) => (c || '').trim().toLowerCase());
    const exact = (name: string) => lower.findIndex((h) => h === name);
    const starts = (prefix: string) => lower.findIndex((h) => h.startsWith(prefix));
    const pick = (idx: number, fallback: number) => (idx >= 0 ? idx : fallback);

    const roomExact = exact('room');
    const buildingExact = exact('building');
    const roomIdx = roomExact >= 0 ? roomExact : buildingExact;
    const groupColumnLabel: Lod100ColumnMap['groupColumnLabel'] =
        roomExact >= 0 ? 'Room' : buildingExact >= 0 ? 'Building' : null;
    const storeyIdx = ['storey', 'story', 'level']
        .map((name) => exact(name))
        .find((idx) => idx >= 0) ?? -1;
    const intExtIdx = starts('int');
    const productionExact = exact('no. production');
    const productionStarts = starts('no. prod');
    const productionIdx =
        productionExact >= 0
            ? productionExact
            : productionStarts >= 0
                ? productionStarts
                : exact('production');
    const bdftRoughIdx = lower.findIndex((h) => h.includes('bdft') && h.includes('rough'));
    const bdftRealIdx = lower.findIndex((h) => h.includes('bdft') && h.includes('real'));
    // Classic LOD100 ROOM tables have a line-number column at 0; Building / no-group do not.
    const hasRoom = roomExact >= 0;
    return {
        roomIdx,
        groupColumnLabel,
        storeyIdx,
        subGroupIdx: pick(exact('sub group'), hasRoom ? 2 : 1),
        nameIdx: pick(exact('name'), hasRoom ? 3 : 2),
        intExtIdx,
        materialIdx: pick(exact('material'), hasRoom ? 5 : 4),
        gradeIdx: exact('grade'),
        qtyIdx: pick(starts('quantity'), hasRoom ? 6 : 6),
        widthIdx: pick(starts('width'), hasRoom ? 7 : 7),
        heightIdx: pick(starts('height'), hasRoom ? 8 : 8),
        realLenIdx: pick(starts('length real'), hasRoom ? 9 : 9),
        roughLenIdx: pick(starts('length rough'), hasRoom ? 10 : 10),
        productionIdx,
        bdftRoughIdx,
        bdftRealIdx,
    };
}

/** Cadwork LOD200.6 footer: blank comma-row or `SUM=` board-foot totals. */
function isLod100TrailerRow(cells: string[], map: Lod100ColumnMap): boolean {
    if (isLod100HeaderCells(cells) || isLod100UnitsCells(cells)) return false;
    if (!cells.some((c) => (c || '').trim())) return true;
    if (/^sum\s*=/i.test((cells[0] || '').trim())) return true;
    const name = (cells[map.nameIdx] || '').trim();
    const width = parseFloat(cells[map.widthIdx] || '') || 0;
    const height = parseFloat(cells[map.heightIdx] || '') || 0;
    const roughLen = parseFloat(cells[map.roughLenIdx] || '') || 0;
    return !name && width <= 0 && height <= 0 && roughLen <= 0;
}

/** When the export has no ROOM column, name the single structure from the 3D file stem. */
function lod100FallbackGroupName(lines: string[]): string {
    const meta = lines.find((l) => /3d\s*file\s*name/i.test(l)) || '';
    const afterComma = meta.split(',').slice(1).join(',').trim();
    const stem = afterComma.replace(/\.3d$/i, '').trim();
    return stem || 'Default';
}

/** Locate the LOD100 embedded header line + delimiter; null when not an LOD100 CSV. */
function splitLod100Lines(csvText: string): {
    lines: string[];
    headerLineIdx: number;
    headerCells: string[];
    delimiter: string;
} | null {
    const lines = csvText.replace(/^\uFEFF/, '').trim().split(/\r?\n/);
    for (let i = 0; i < lines.length; i++) {
        const delimiter = detectTimberCsvDelimiter(lines[i]!);
        const headerCells = parseCSVLine(lines[i]!, delimiter);
        if (isLod100HeaderCells(headerCells)) {
            return { lines, headerLineIdx: i, headerCells, delimiter };
        }
    }
    // Cadwork always puts the column header immediately above the [inch] units row.
    for (let i = 0; i < lines.length; i++) {
        const delimiter = detectTimberCsvDelimiter(lines[i]!);
        const cells = parseCSVLine(lines[i]!, delimiter);
        if (!isLod100UnitsCells(cells)) continue;
        for (let j = i - 1; j >= 0; j--) {
            if (!lines[j]!.trim()) continue;
            const headerDelimiter = detectTimberCsvDelimiter(lines[j]!);
            const headerCells = parseCSVLine(lines[j]!, headerDelimiter);
            return { lines, headerLineIdx: j, headerCells, delimiter: headerDelimiter };
        }
    }
    return null;
}

/**
 * Order length in feet. Decimal LOD100/LOD200 cells are inches (`72` → 6').
 * Imperial Cadwork cells already include a foot mark (`14'`, `13'-5"`) and must
 * not be divided by 12. The units row still prints `[inch]` for both styles.
 */
function parseLod100OrderLengthFeet(raw: string): number {
    const trimmed = (raw || '').trim();
    if (!trimmed) return 0;
    if (trimmed.includes("'")) {
        return parseFeetInches(trimmed);
    }
    const inches = parseFloat(trimmed) || 0;
    return Math.round((inches / 12) * 100) / 100;
}

/** Parse a Cadwork LOD100 timber CSV (inch-based lengths, ROOM-grouped). */
export function parseLod100TimberCsv(csvText: string, options?: TimberCsvParseOptions): CSVParseResult {
    const split = splitLod100Lines(csvText);
    const errors: string[] = [];
    const items: ParsedTimberItem[] = [];
    const groupsSet = new Set<string>();

    if (!split) {
        return {
            items: [],
            groups: [],
            csvType: 'materials',
            totalItems: 0,
            errors: ['LOD100 header row (Sub group / Name) not found.'],
            debugInfo: { totalLines: 0, dataLines: 0, delimiter: 'unknown', sampleRow: [] },
        };
    }

    const { lines, headerLineIdx, headerCells, delimiter } = split;
    const map = lod100ColumnMapFromHeader(headerCells);
    const fallbackGroup = lod100FallbackGroupName(lines);
    const dataLines = lines.slice(headerLineIdx + 1);
    let sequentialLine = 0;
    let sampleRow: string[] = [];

    dataLines.forEach((line, index) => {
        if (!line.trim()) return;
        const cells = parseCSVLine(line, delimiter);
        if (isLod100UnitsCells(cells)) return;
        if (isLod100TrailerRow(cells, map)) return;

        const rowLabel = `Row ${headerLineIdx + index + 2}`;
        const room = map.roomIdx >= 0 ? (cells[map.roomIdx] || '').trim() : '';
        const storey = map.storeyIdx >= 0 ? (cells[map.storeyIdx] || '').trim() : '';
        const subGroup = (cells[map.subGroupIdx] || '').trim();
        const name = (cells[map.nameIdx] || '').trim();
        const rawMaterial = (cells[map.materialIdx] || '').trim();
        const grade = map.gradeIdx >= 0 ? (cells[map.gradeIdx] || '').trim() : '';
        const material = grade && !rawMaterial.toLowerCase().includes(grade.toLowerCase())
            ? `${rawMaterial} - ${grade}`
            : rawMaterial;
        const quantity = parseInt(cells[map.qtyIdx] || '', 10) || 1;
        const rawWidth = parseTimberCrossSectionInches(cells[map.widthIdx]);
        const rawHeight = parseTimberCrossSectionInches(cells[map.heightIdx]);
        const orderLength = parseLod100OrderLengthFeet(cells[map.roughLenIdx] || '');
        const section = crossSectionFields(rawHeight, rawWidth, orderLength, options?.trueDimensions);
        const productionRaw = map.productionIdx >= 0 ? (cells[map.productionIdx] || '').trim() : '';
        const productionNumber = productionRaw || undefined;

        if (!name || section.height <= 0 || section.width <= 0 || orderLength <= 0) {
            errors.push(
                `${rowLabel}: Missing data (name="${name}", h=${section.height}, w=${section.width}, len=${orderLength})`,
            );
            return;
        }

        sequentialLine += 1;
        if (sampleRow.length === 0) sampleRow = cells;
        const fromCol = parseInt(cells[0] || '', 10);
        const lineNumber = !isNaN(fromCol) && fromCol > 0 ? fromCol : sequentialLine;

        const group = room || fallbackGroup;
        const pieceName = [storey, subGroup, name].filter(Boolean).join(' — ');

        groupsSet.add(group);
        items.push({
            pieceName,
            group,
            material,
            quantity,
            orderLength,
            lineNumber,
            ...section,
            ...(productionNumber ? { productionNumber } : {}),
            ...(storey ? { storey } : {}),
        });
    });

    return {
        items,
        groups: Array.from(groupsSet).sort(),
        csvType: 'materials',
        totalItems: items.length,
        errors,
        debugInfo: {
            totalLines: lines.length,
            dataLines: dataLines.filter((l) => l.trim()).length,
            delimiter: delimiter === '\t' ? 'TAB' : 'COMMA',
            sampleRow,
        },
    };
}

/** Supplier RFQ export from proposal (Structure,Name,Species,Grade,Dryness,Dimensions,...). */
export function isSupplierTimberExportCsv(csvText: string): boolean {
    const header = csvText.replace(/^\uFEFF/, '').trim().split(/\r?\n/)[0]?.toLowerCase() || '';
    return (
        header.includes('structure')
        && header.includes('name')
        && header.includes('dimensions')
        && (header.includes('board feet') || header.includes('boardfeet'))
    );
}

function parseSupplierDimensionsCell(
    raw: string,
    options?: TimberCsvParseOptions,
): { height: number; width: number; orderLength: number; normalizedDimensions: string; boardFeet: number; trueDimensions?: boolean } | null {
    const dim = (raw || '').trim();
    if (!dim) return null;
    const parts = dim.split('x').map((p) => parseFloat(p.trim()));
    if (parts.length < 3 || parts.some((n) => !Number.isFinite(n) || n <= 0)) return null;
    const [a, b, len] = parts;
    const section = crossSectionFields(a!, b!, len!, options?.trueDimensions);
    if (section.height <= 0 || section.width <= 0) return null;
    return { ...section, orderLength: len! };
}

function supplierMaterialFromCells(species: string, grade: string): string {
    const s = (species || '').trim();
    const g = (grade || '').trim();
    if (s && g) return `${s} - ${g}`;
    return s || g;
}

/** Parse supplier-format timber CSV produced by buildTimberSupplierCsvText / push staging. */
export function parseSupplierTimberExportCsv(csvText: string, options?: TimberCsvParseOptions): CSVParseResult {
    const lines = csvText.replace(/^\uFEFF/, '').trim().split(/\r?\n/).filter((l) => l.trim());
    const errors: string[] = [];
    const items: ParsedTimberItem[] = [];
    const groupsSet = new Set<string>();

    if (lines.length < 2) {
        return {
            items: [],
            groups: [],
            csvType: 'materials',
            totalItems: 0,
            errors: ['No data rows found in supplier timber CSV.'],
            debugInfo: { totalLines: lines.length, dataLines: 0, delimiter: 'COMMA', sampleRow: [] },
        };
    }

    const header = parseCSVLine(lines[0]!, ',').map((c) => c.trim().toLowerCase());
    const idx = (name: string) => header.findIndex((h) => h === name);
    const structureIdx = idx('structure');
    const nameIdx = idx('name');
    const speciesIdx = idx('species') >= 0 ? idx('species') : idx('material');
    const gradeIdx = idx('grade');
    const dimensionsIdx = idx('dimensions');
    const qtyIdx = idx('quantity');
    const bdftIdx = header.findIndex((h) => h === 'board feet' || h === 'boardfeet');

    if (structureIdx < 0 || nameIdx < 0 || dimensionsIdx < 0) {
        return {
            items: [],
            groups: [],
            csvType: 'materials',
            totalItems: 0,
            errors: ['Unrecognized supplier timber CSV header.'],
            debugInfo: { totalLines: lines.length, dataLines: lines.length - 1, delimiter: 'COMMA', sampleRow: header },
        };
    }

    let lineNumber = 0;
    for (let rowIdx = 1; rowIdx < lines.length; rowIdx += 1) {
        const columns = parseCSVLine(lines[rowIdx]!, ',');
        const rowLabel = `Row ${rowIdx + 1}`;
        const structure = (columns[structureIdx] || '').trim();
        const pieceName = (columns[nameIdx] || '').trim();
        const species = speciesIdx >= 0 ? (columns[speciesIdx] || '').trim() : '';
        const grade = gradeIdx >= 0 ? (columns[gradeIdx] || '').trim() : '';
        const material = supplierMaterialFromCells(species, grade);
        const quantity = qtyIdx >= 0 ? parseInt(columns[qtyIdx] || '1', 10) || 1 : 1;
        const dims = parseSupplierDimensionsCell(columns[dimensionsIdx] || '', options);
        if (!structure || !pieceName || !dims) {
            errors.push(
                `${rowLabel}: Missing data (structure="${structure}", name="${pieceName}", dimensions="${columns[dimensionsIdx] || ''}")`,
            );
            continue;
        }
        lineNumber += 1;
        const bdftRaw = bdftIdx >= 0 ? parseFloat(columns[bdftIdx] || '') : NaN;
        const boardFeet = Number.isFinite(bdftRaw) && bdftRaw > 0 ? bdftRaw : dims.boardFeet;
        groupsSet.add(structure);
        items.push({
            pieceName,
            group: structure,
            material,
            quantity,
            height: dims.height,
            width: dims.width,
            orderLength: dims.orderLength,
            normalizedDimensions: dims.normalizedDimensions,
            boardFeet,
            lineNumber,
            ...(dims.trueDimensions ? { trueDimensions: true } : {}),
        });
    }

    return {
        items,
        groups: Array.from(groupsSet).sort(),
        csvType: 'materials',
        totalItems: items.length,
        errors,
        debugInfo: {
            totalLines: lines.length,
            dataLines: lines.length - 1,
            delimiter: 'COMMA',
            sampleRow: parseCSVLine(lines[1]!, ','),
        },
    };
}

/** Parse timber CSV for RFQ hub staging / publish (Cadwork tally or supplier export). */
export function parseTimberCsvForRfqHub(csvText: string, options?: TimberCsvParseOptions): CSVParseResult {
    if (isSupplierTimberExportCsv(csvText)) {
        return parseSupplierTimberExportCsv(csvText, options);
    }
    return parseProposalCSV(csvText, options);
}

function isNumericCol(value: string): boolean {
    return /^\d+$/.test((value || '').trim());
}

function isTextCol(value: string): boolean {
    return /[a-zA-Z]/.test((value || '').trim());
}

/** Detect column layout from the first one or two data rows. */
export function detectTimberCsvColumnLayout(
    firstRow: string[],
    delimiter: string,
    secondRow?: string[],
): TimberCsvColumnLayout {
    const cols = firstRow.map((c) => (c || '').trim());
    const cols2 = secondRow?.map((c) => (c || '').trim());

    let hasLineNumberCol = false;
    let hasProductionCol = false;
    let productionIdx = -1;
    let nameIdx = 0;

    const col0Num = cols.length > 0 && isNumericCol(cols[0]);
    const col1Num = cols.length > 1 && isNumericCol(cols[1]);
    const col1Text = cols.length > 1 && isTextCol(cols[1]);
    const col0Text = cols.length > 0 && isTextCol(cols[0]);

    const looksSequentialLineNumbers =
        col0Num &&
        cols[0] === '1' &&
        cols2 &&
        cols2.length > 0 &&
        isNumericCol(cols2[0]) &&
        parseInt(cols2[0], 10) === 2;

    if (looksSequentialLineNumbers) {
        hasLineNumberCol = true;
        if (col1Num) {
            hasProductionCol = true;
            productionIdx = 1;
            nameIdx = 2;
        } else if (col1Text) {
            nameIdx = 1;
        }
    } else if (col0Num && col1Text) {
        const leadingNum = parseInt(cols[0], 10);
        if (leadingNum > 0 && leadingNum < 1000) {
            // Small leading integer + piece name — line# (typical on tally exports)
            hasLineNumberCol = true;
            nameIdx = 1;
        } else {
            // Legacy Cadwork 9-column: prod#, name, group, ...
            hasProductionCol = true;
            productionIdx = 0;
            nameIdx = 1;
        }
    } else if (col0Text) {
        // 8-column: name, group, ...
        nameIdx = 0;
    } else if (col0Num && col1Num) {
        // Line# + prod# without sequential check on row 2 (single row file)
        hasLineNumberCol = true;
        hasProductionCol = true;
        productionIdx = 1;
        nameIdx = 2;
    }

    const materialIdx = nameIdx + 2;
    const groupIdx = nameIdx + 1;
    const materialCell = (cols[materialIdx] || '').trim();
    const afterMaterial = (cols[materialIdx + 1] || '').trim();
    const afterGrade = (cols[materialIdx + 2] || '').trim();
    const hasSeparateGradeCol =
        !!materialCell
        && !materialHasInlineGradeSuffix(materialCell)
        && isTimberGradeSuffixCell(afterMaterial)
        && /^\d+$/.test(afterGrade);

    if (hasSeparateGradeCol) {
        return {
            delimiter,
            hasLineNumberCol,
            hasProductionCol,
            productionIdx,
            nameIdx,
            groupIdx,
            materialIdx,
            hasSeparateGradeCol: true,
            gradeIdx: materialIdx + 1,
            qtyIdx: materialIdx + 2,
            heightIdx: materialIdx + 3,
            widthIdx: materialIdx + 4,
            orderLenIdx: nameIdx + 7,
        };
    }

    return {
        delimiter,
        hasLineNumberCol,
        hasProductionCol,
        productionIdx,
        nameIdx,
        groupIdx,
        materialIdx,
        hasSeparateGradeCol: false,
        gradeIdx: -1,
        qtyIdx: nameIdx + 3,
        heightIdx: nameIdx + 4,
        widthIdx: nameIdx + 5,
        orderLenIdx: nameIdx + 7,
    };
}

/** Width of one timber record on a row (line# through order length). */
export function timberRecordColumnWidth(layout: TimberCsvColumnLayout): number {
    return layout.orderLenIdx + 1;
}

/**
 * Cadwork sometimes exports multiple timber pieces on one physical row (wide matrix).
 * Split those side-by-side records into one row per piece for preview and import.
 */
export function expandWideTimberCsvRows(
    rows: string[][],
    layout: TimberCsvColumnLayout,
): string[][] {
    const firstWidth = timberRecordColumnWidth(layout);
    const continuationWidth =
        layout.hasLineNumberCol || layout.hasProductionCol ? firstWidth - 1 : firstWidth;
    const expanded: string[][] = [];

    for (const rawRow of rows) {
        const cells = rawRow.map((c) => (c || '').trim());
        if (!cells.some(Boolean)) continue;

        if (cells.length <= firstWidth) {
            expanded.push(cells.slice(0, firstWidth));
            continue;
        }

        let offset = 0;
        let chunkIndex = 0;
        let pendingLineNum: string | undefined;

        while (offset < cells.length) {
            const width = chunkIndex === 0 ? firstWidth : continuationWidth;
            const tail = cells.slice(offset);
            if (!tail.some(Boolean)) break;

            if (chunkIndex > 0 && tail.length < continuationWidth) break;

            let slice =
                chunkIndex === 0
                    ? cells.slice(offset, offset + firstWidth)
                    : cells.slice(offset, offset + continuationWidth);

            if (chunkIndex > 0 && layout.hasLineNumberCol) {
                slice = [pendingLineNum ?? '', ...slice];
                pendingLineNum = undefined;
            } else if (chunkIndex > 0 && layout.hasProductionCol && pendingLineNum) {
                slice = [pendingLineNum, ...slice];
                pendingLineNum = undefined;
            }

            if (slice.length > layout.orderLenIdx) {
                const orderVal = slice[layout.orderLenIdx] ?? '';
                const glued = orderVal.match(/^(.+["'])\s+(\d+)$/);
                if (glued) {
                    slice[layout.orderLenIdx] = glued[1];
                    pendingLineNum = glued[2];
                }
            }

            if (slice.some(Boolean)) {
                const padded = [...slice];
                while (padded.length < firstWidth) padded.push('');
                expanded.push(padded.slice(0, firstWidth));
            }

            offset += width;
            chunkIndex += 1;
        }
    }

    return expanded;
}

/** True when a CSV row is a structure separator (line index only, no timber data). */
export function isTimberCsvStructureBreakRow(
    cells: string[],
    layout: TimberCsvColumnLayout,
): boolean {
    const pieceName = (cells[layout.nameIdx] || '').trim();
    if (pieceName) return false;

    const material = (cells[layout.materialIdx] || '').trim();
    const height = (cells[layout.heightIdx] || '').trim();
    const width = (cells[layout.widthIdx] || '').trim();
    const orderLen = (cells[layout.orderLenIdx] || '').trim();

    return !material && !height && !width && !orderLen;
}

export type TimberCsvPreviewColumn = {
    label: string;
    align: 'left' | 'right';
    kind: 'text' | 'number' | 'dimension';
};

/** Human-readable headers for timber CSV preview tables (from detected column layout). */
export function buildTimberCsvColumnHeaders(
    layout: TimberCsvColumnLayout,
    colCount: number,
): TimberCsvPreviewColumn[] {
    const byIdx = new Map<number, TimberCsvPreviewColumn>();

    if (layout.hasLineNumberCol) {
        byIdx.set(0, { label: '#', align: 'right', kind: 'number' });
    }
    if (layout.hasProductionCol && layout.productionIdx >= 0) {
        byIdx.set(layout.productionIdx, { label: 'Prod #', align: 'right', kind: 'number' });
    }
    byIdx.set(layout.nameIdx, { label: 'Piece', align: 'left', kind: 'text' });
    byIdx.set(layout.groupIdx, { label: 'Group', align: 'left', kind: 'text' });
    byIdx.set(layout.materialIdx, { label: 'Material', align: 'left', kind: 'text' });
    byIdx.set(layout.qtyIdx, { label: 'Qty', align: 'right', kind: 'number' });
    byIdx.set(layout.heightIdx, { label: 'H', align: 'right', kind: 'dimension' });
    byIdx.set(layout.widthIdx, { label: 'W', align: 'right', kind: 'dimension' });
    const realLenIdx = layout.orderLenIdx - 1;
    if (realLenIdx > layout.widthIdx) {
        byIdx.set(realLenIdx, { label: 'Length', align: 'right', kind: 'dimension' });
    }
    byIdx.set(layout.orderLenIdx, { label: 'Order', align: 'right', kind: 'dimension' });

    return Array.from({ length: colCount }, (_, i) =>
        byIdx.get(i) ?? { label: `Col ${i + 1}`, align: 'left' as const, kind: 'text' as const },
    );
}

/** Preview rows are LOD100 when the embedded header row survives near the top. */
function findLod100PreviewHeader(rows: string[][]): { headerRowIdx: number; headerCells: string[] } | null {
    for (let i = 0; i < Math.min(3, rows.length); i++) {
        if (isLod100HeaderCells(rows[i] || [])) {
            return { headerRowIdx: i, headerCells: rows[i]! };
        }
    }
    return null;
}

/** Display column headers for LOD100 preview tables (without Species/Grade enrichment). */
function buildLod100ColumnHeaders(map: Lod100ColumnMap, colCount: number): TimberCsvPreviewColumn[] {
    const byIdx = new Map<number, TimberCsvPreviewColumn>();
    // LOD100 ROOM tables start with a line-number column. LOD200 Building tables
    // put the grouping name in column 0 — do not label that column '#'.
    if (map.roomIdx !== 0) {
        byIdx.set(0, { label: '#', align: 'right', kind: 'number' });
    }
    if (map.roomIdx >= 0) {
        byIdx.set(map.roomIdx, { label: map.groupColumnLabel || 'Room', align: 'left', kind: 'text' });
    }
    if (map.storeyIdx >= 0) {
        byIdx.set(map.storeyIdx, { label: 'Level', align: 'left', kind: 'text' });
    }
    byIdx.set(map.subGroupIdx, { label: 'Sub group', align: 'left', kind: 'text' });
    if (map.productionIdx >= 0) {
        byIdx.set(map.productionIdx, { label: 'Prod #', align: 'right', kind: 'number' });
    }
    byIdx.set(map.nameIdx, { label: 'Piece', align: 'left', kind: 'text' });
    if (map.intExtIdx >= 0) {
        byIdx.set(map.intExtIdx, { label: 'Int/Ext', align: 'left', kind: 'text' });
    }
    byIdx.set(map.materialIdx, { label: 'Material', align: 'left', kind: 'text' });
    if (map.gradeIdx >= 0) byIdx.set(map.gradeIdx, { label: 'Grade', align: 'left', kind: 'text' });
    byIdx.set(map.qtyIdx, { label: 'Qty', align: 'right', kind: 'number' });
    byIdx.set(map.widthIdx, { label: 'W', align: 'right', kind: 'dimension' });
    byIdx.set(map.heightIdx, { label: 'H', align: 'right', kind: 'dimension' });
    byIdx.set(map.realLenIdx, { label: 'Real len', align: 'right', kind: 'dimension' });
    byIdx.set(map.roughLenIdx, { label: 'Rough len', align: 'right', kind: 'dimension' });
    if (map.bdftRoughIdx >= 0) {
        byIdx.set(map.bdftRoughIdx, { label: 'BDFT rough', align: 'right', kind: 'number' });
    }
    if (map.bdftRealIdx >= 0) {
        byIdx.set(map.bdftRealIdx, { label: 'BDFT real', align: 'right', kind: 'number' });
    }

    return Array.from({ length: colCount }, (_, i) =>
        byIdx.get(i) ?? { label: `Col ${i + 1}`, align: 'left' as const, kind: 'text' as const },
    );
}

/** LOD100 preview display: drop header/units rows, insert Species + Grade after Material. */
function resolveLod100PreviewDisplay(
    rows: string[][],
    headerCells: string[],
): { rows: string[][]; columns: TimberCsvPreviewColumn[] } {
    const map = lod100ColumnMapFromHeader(headerCells);
    const colCount = Math.max(...rows.map((r) => r.length), headerCells.length, 1);
    const baseColumns = buildLod100ColumnHeaders(map, colCount);

    const insertAt = map.materialIdx + 1;
    const hasNativeGrade = map.gradeIdx >= 0;
    const columns = hasNativeGrade
        ? [
            ...baseColumns.slice(0, insertAt),
            { label: 'Species', align: 'left' as const, kind: 'text' as const },
            ...baseColumns.slice(insertAt),
        ]
        : [
            ...baseColumns.slice(0, insertAt),
            { label: 'Species', align: 'left' as const, kind: 'text' as const },
            { label: 'Grade', align: 'left' as const, kind: 'text' as const },
            ...baseColumns.slice(insertAt),
        ];

    const bodyRows = rows.filter(
        (cells) =>
            !isLod100HeaderCells(cells)
            && !isLod100UnitsCells(cells)
            && !isLod100TrailerRow(cells, map),
    );

    const enrichedRows = bodyRows.map((cells) => {
        const rawMaterial = (cells[map.materialIdx] || '').trim().replace(/^"|"$/g, '');
        const rawGrade = hasNativeGrade ? (cells[map.gradeIdx] || '').trim() : '';
        const { species, grade } = parseTimberMaterialColumn(
            rawGrade ? `${rawMaterial} - ${rawGrade}` : rawMaterial,
            {
                speciesFallback: DEFAULT_PROPOSAL_TIMBER_SPECIES,
                gradeFallback: DEFAULT_PROPOSAL_TIMBER_GRADE,
            },
        );
        const padded = cells.map((c) => (c || '').trim());
        while (padded.length < colCount) padded.push('');
        if (hasNativeGrade) {
            padded.splice(insertAt, 0, species);
            if (map.gradeIdx >= 0) padded[map.gradeIdx + 1] = grade;
        } else {
            padded.splice(insertAt, 0, species, grade);
        }
        return padded;
    });

    return { rows: enrichedRows, columns };
}

/** Detect layout + headers for raw preview rows (post-metadata data lines). */
export function resolveTimberCsvPreviewColumns(rows: string[][]): TimberCsvPreviewColumn[] {
    if (!rows.length) return [];
    const lod100 = findLod100PreviewHeader(rows);
    if (lod100) {
        const map = lod100ColumnMapFromHeader(lod100.headerCells);
        const colCount = Math.max(...rows.map((r) => r.length), 1);
        return buildLod100ColumnHeaders(map, colCount);
    }
    const colCount = Math.max(...rows.map((r) => r.length), 1);
    const first = rows[0].map((c) => (c || '').trim());
    const second = rows[1]?.map((c) => (c || '').trim());
    const delimiter = detectTimberCsvDelimiter(first);
    const layout = detectTimberCsvColumnLayout(first, delimiter, second);
    return buildTimberCsvColumnHeaders(layout, colCount);
}

/**
 * Preview table rows/columns with parsed Species + Grade inserted after Material.
 * Keeps the raw Material cell and adds CRM-resolved species/grade for verification.
 * Height and width are rounded to the nearest inch unless true dimensions is set.
 */
export function resolveTimberCsvPreviewDisplay(
    rows: string[][],
    options?: TimberCsvParseOptions,
): {
    rows: string[][];
    columns: TimberCsvPreviewColumn[];
} {
    if (!rows.length) return { rows: [], columns: [] };

    const lod100 = findLod100PreviewHeader(rows);
    const display = lod100
        ? resolveLod100PreviewDisplay(rows, lod100.headerCells)
        : resolveClassicTimberCsvPreviewDisplay(rows);

    return {
        columns: display.columns,
        rows: applyPreviewCrossSections(display.rows, display.columns, options?.trueDimensions),
    };
}

function formatPreviewCrossSectionCell(raw: string, trueDimensions?: boolean): string {
    const trimmed = (raw || '').trim();
    if (!trimmed) return raw;
    const inches = parseTimberCrossSectionInches(trimmed);
    if (!Number.isFinite(inches) || inches <= 0) return raw;
    return String(formatTimberCrossSectionInches(inches, trueDimensions));
}

function applyPreviewCrossSections(
    rows: string[][],
    columns: TimberCsvPreviewColumn[],
    trueDimensions?: boolean,
): string[][] {
    const idxs: number[] = [];
    columns.forEach((col, i) => {
        if (col.label === 'H' || col.label === 'W') idxs.push(i);
    });
    if (!idxs.length) return rows;
    return rows.map((row) => {
        const next = row.slice();
        for (const i of idxs) {
            if (i < next.length && (next[i] || '').trim()) {
                next[i] = formatPreviewCrossSectionCell(next[i] || '', trueDimensions);
            }
        }
        return next;
    });
}

function resolveClassicTimberCsvPreviewDisplay(rows: string[][]): {
    rows: string[][];
    columns: TimberCsvPreviewColumn[];
} {
    const first = rows[0].map((c) => (c || '').trim());
    const second = rows[1]?.map((c) => (c || '').trim());
    const delimiter = detectTimberCsvDelimiter(first);
    const layout = detectTimberCsvColumnLayout(first, delimiter, second);
    const expandedRows = expandWideTimberCsvRows(rows, layout);
    const colCount = timberRecordColumnWidth(layout);
    const baseColumns = buildTimberCsvColumnHeaders(layout, colCount);

    const insertAt = layout.materialIdx + 1;
    const speciesCol: TimberCsvPreviewColumn = { label: 'Species', align: 'left', kind: 'text' };
    const gradeCol: TimberCsvPreviewColumn = { label: 'Grade', align: 'left', kind: 'text' };
    const columns = [
        ...baseColumns.slice(0, insertAt),
        speciesCol,
        gradeCol,
        ...baseColumns.slice(insertAt),
    ];

    const enrichedRows = expandedRows.map((cells) => {
        const trimmed = cells.slice(0, colCount);
        const isBreak = isTimberCsvStructureBreakRow(trimmed, layout);
        const rawMaterial = (trimmed[layout.materialIdx] || '').trim().replace(/^"|"$/g, '');
        const { species, grade } = isBreak
            ? { species: '', grade: '' }
            : parseTimberMaterialColumn(rawMaterial, {
                  speciesFallback: DEFAULT_PROPOSAL_TIMBER_SPECIES,
                  gradeFallback: DEFAULT_PROPOSAL_TIMBER_GRADE,
              });
        const padded = [...trimmed];
        while (padded.length < colCount) padded.push('');
        padded.splice(insertAt, 0, species, grade);
        return padded;
    });

    return { rows: enrichedRows, columns };
}

// Parse inches value like "4"", "7.5", or "5 1/2""
function parseInches(value: string): number {
    if (!value) return 0;
    const cleaned = value.replace(/["']/g, '').trim();
    return parseInchFractionPart(cleaned);
}

/** Inches portion after a foot mark: "5", "5 1/2", etc. */
function parseInchFractionPart(raw: string): number {
    const cleaned = raw.replace(/"/g, '').trim();
    if (!cleaned) return 0;
    const withFraction = cleaned.match(/^(\d+(?:\.\d+)?)\s+(\d+)\/(\d+)$/);
    if (withFraction) {
        return parseFloat(withFraction[1]!) + parseInt(withFraction[2]!, 10) / parseInt(withFraction[3]!, 10);
    }
    return parseFloat(cleaned) || 0;
}

/**
 * H/W cross-section when Cadwork uses feet+inches (e.g. 1'-5", 4'-5") instead of inch-only (17").
 * Only invoked when the cell contains a foot mark — normal inch-only Cadwork keeps {@link parseInches}.
 */
function parseFeetInchCrossSectionToInches(value: string): number {
    const cleaned = value.trim();
    if (!cleaned) return 0;

    const simpleFeet = cleaned.match(/^(\d+)'$/);
    if (simpleFeet) {
        return parseInt(simpleFeet[1]!, 10) * 12;
    }

    const feetInchMatch = cleaned.match(/^(\d+)'\s*-?\s*(.*)$/);
    if (feetInchMatch) {
        const feet = parseInt(feetInchMatch[1]!, 10);
        return feet * 12 + parseInchFractionPart(feetInchMatch[2] || '');
    }

    return parseInches(cleaned);
}

/** Timber H or W column — inch-only by default; feet+inch when the cell includes a foot mark. */
function parseTimberCrossSectionInches(value: string | undefined): number {
    if (!value) return 0;
    const trimmed = value.trim();
    if (!trimmed.includes("'")) {
        return parseInches(trimmed);
    }
    return parseFeetInchCrossSectionToInches(trimmed);
}

// Parse feet-inches format like "4'" or "12'-1 9/16"" or "3'-10 13/16""
function parseFeetInches(value: string): number {
    if (!value) return 0;
    const cleaned = value.trim();

    const simpleFeetMatch = cleaned.match(/^(\d+)'$/);
    if (simpleFeetMatch) {
        return parseInt(simpleFeetMatch[1], 10);
    }

    const feetInchMatch = cleaned.match(/^(\d+)'-?(.*)$/);
    if (feetInchMatch) {
        const feet = parseInt(feetInchMatch[1], 10);
        return feet;
    }

    const plainNum = parseFloat(cleaned.replace(/['"]/g, ''));
    if (!isNaN(plainNum)) {
        return plainNum;
    }

    return 0;
}

function materialHasInlineGradeSuffix(material: string): boolean {
    const trimmed = material.trim();
    return trimmed.includes(' - ') || trimmed.includes(' – ') || trimmed.includes(' — ');
}

/** Resolve material + column indices for one row (handles per-row separate Grade column). */
function resolveRowTimberParseIndices(
    columns: string[],
    layout: TimberCsvColumnLayout,
): {
    material: string;
    qtyIdx: number;
    heightIdx: number;
    widthIdx: number;
    orderLenIdx: number;
} {
    let material = columns[layout.materialIdx]?.trim() || '';
    let qtyIdx = layout.qtyIdx;
    let heightIdx = layout.heightIdx;
    let widthIdx = layout.widthIdx;
    let orderLenIdx = layout.orderLenIdx;

    if (layout.hasSeparateGradeCol && layout.gradeIdx >= 0) {
        const gradeCell = columns[layout.gradeIdx]?.trim() || '';
        if (gradeCell && !materialHasInlineGradeSuffix(material)) {
            material = `${material} - ${gradeCell}`;
        }
        return { material, qtyIdx, heightIdx, widthIdx, orderLenIdx };
    }

    const gradeCandidate = columns[layout.materialIdx + 1]?.trim() || '';
    const qtyCandidate = columns[layout.materialIdx + 2]?.trim() || '';
    if (
        material
        && !materialHasInlineGradeSuffix(material)
        && isTimberGradeSuffixCell(gradeCandidate)
        && /^\d+$/.test(qtyCandidate)
    ) {
        material = `${material} - ${gradeCandidate}`;
        qtyIdx = layout.materialIdx + 2;
        heightIdx = layout.materialIdx + 3;
        widthIdx = layout.materialIdx + 4;
        orderLenIdx = layout.materialIdx + 5;
    }

    return { material, qtyIdx, heightIdx, widthIdx, orderLenIdx };
}

function parseOrderLengthFromColumns(columns: string[], startIdx: number): number {
    for (let idx = columns.length - 1; idx >= startIdx; idx -= 1) {
        const len = parseFeetInches(columns[idx]);
        if (len > 0) return len;
    }
    return 0;
}

function parseDataRow(
    columns: string[],
    layout: TimberCsvColumnLayout,
    lineNumber: number,
    rowErrors: string[],
    rowLabel: string,
    options?: TimberCsvParseOptions,
): ParsedTimberItem | null {
    const pieceName = columns[layout.nameIdx]?.trim() || '';
    const group = columns[layout.groupIdx]?.trim() || 'Default';
    const { material, qtyIdx, heightIdx, widthIdx } = resolveRowTimberParseIndices(
        columns,
        layout,
    );
    const quantity = parseInt(columns[qtyIdx]) || 1;
    const rawHeight = parseTimberCrossSectionInches(columns[heightIdx]);
    const rawWidth = parseTimberCrossSectionInches(columns[widthIdx]);
    const orderLength = parseOrderLengthFromColumns(columns, widthIdx + 1);
    const section = crossSectionFields(rawHeight, rawWidth, orderLength, options?.trueDimensions);

    if (!pieceName || section.height === 0 || section.width === 0 || orderLength === 0) {
        rowErrors.push(
            `${rowLabel}: Missing data (name="${pieceName}", h=${section.height}, w=${section.width}, len=${orderLength})`,
        );
        return null;
    }

    let productionNumber: string | undefined;
    if (layout.hasProductionCol && layout.productionIdx >= 0) {
        const prod = columns[layout.productionIdx]?.trim();
        if (prod) productionNumber = prod;
    }

    let assignedLineNumber = lineNumber;
    if (layout.hasLineNumberCol) {
        const fromCol = parseInt(columns[0], 10);
        if (!isNaN(fromCol) && fromCol > 0) assignedLineNumber = fromCol;
    }

    return {
        pieceName,
        group,
        material,
        quantity,
        orderLength,
        lineNumber: assignedLineNumber,
        productionNumber,
        ...section,
    };
}

export function parseProposalCSV(csvText: string, options?: TimberCsvParseOptions): CSVParseResult {
    if (isCadworkSteelCsv(csvText)) {
        return {
            items: [],
            groups: [],
            csvType: 'materials',
            totalItems: 0,
            errors: ['This file is a steel/hardware list, not a timber list.'],
            debugInfo: { totalLines: 0, dataLines: 0, delimiter: ',', sampleRow: [] },
        };
    }

    // LOD100 exports share the Cadwork metadata header but use a different
    // column order and inch-based lengths — route before classic layout detection.
    if (isLod100TimberCsv(csvText)) {
        return parseLod100TimberCsv(csvText, options);
    }

    const lines = csvText.trim().split(/\r?\n/);
    const errors: string[] = [];
    const items: ParsedTimberItem[] = [];
    const groupsSet = new Set<string>();

    const dataLines = lines.slice(METADATA_ROW_COUNT);

    if (dataLines.length === 0) {
        return {
            items: [],
            groups: [],
            csvType: 'materials',
            totalItems: 0,
            errors: ['No data rows found after skipping first 3 rows'],
            debugInfo: { totalLines: lines.length, dataLines: 0, delimiter: 'unknown', sampleRow: [] },
        };
    }

    const firstDataLine = dataLines.find((l) => l.trim()) || dataLines[0];
    const tabCount = (firstDataLine.match(/\t/g) || []).length;
    const commaCount = (firstDataLine.match(/,/g) || []).length;
    const delimiter = tabCount > commaCount ? '\t' : ',';

    const firstRow = parseCSVLine(firstDataLine, delimiter);
    const secondDataLine = dataLines.find((l) => l.trim() && l !== firstDataLine);
    const secondRow = secondDataLine ? parseCSVLine(secondDataLine, delimiter) : undefined;
    const layout = detectTimberCsvColumnLayout(firstRow, delimiter, secondRow);

    let sequentialLine = 0;
    dataLines.forEach((line, index) => {
        if (!line.trim()) return;

        const columns = parseCSVLine(line, delimiter);
        const rowChunks = expandWideTimberCsvRows([columns], layout);

        rowChunks.forEach((chunk) => {
            sequentialLine += 1;

            try {
                const item = parseDataRow(
                    chunk,
                    layout,
                    sequentialLine,
                    errors,
                    `Row ${index + METADATA_ROW_COUNT + 1}`,
                    options,
                );
                if (!item) return;
                groupsSet.add(item.group);
                items.push(item);
            } catch (err) {
                errors.push(`Row ${index + METADATA_ROW_COUNT + 1}: Parse error - ${err}`);
            }
        });
    });

    return {
        items,
        groups: Array.from(groupsSet).sort(),
        csvType: 'materials',
        totalItems: items.length,
        errors,
        debugInfo: {
            totalLines: lines.length,
            dataLines: dataLines.length,
            delimiter: delimiter === '\t' ? 'TAB' : 'COMMA',
            sampleRow: firstRow,
        },
    };
}

/** Rewrite timber CSV with a leading Line# column; preserves prod# when present. */
export function enrichTimberCsvText(csvText: string): string {
    // LOD100 exports already carry sequential line numbers in column 0.
    if (isLod100TimberCsv(csvText)) return csvText;

    const lines = csvText.trim().split(/\r?\n/);
    if (lines.length <= METADATA_ROW_COUNT) return csvText;

    const metadata = lines.slice(0, METADATA_ROW_COUNT);
    const dataLines = lines.slice(METADATA_ROW_COUNT);
    const firstDataLine = dataLines.find((l) => l.trim());
    if (!firstDataLine) return csvText;

    const tabCount = (firstDataLine.match(/\t/g) || []).length;
    const commaCount = (firstDataLine.match(/,/g) || []).length;
    const delimiter = tabCount > commaCount ? '\t' : ',';
    const firstRow = parseCSVLine(firstDataLine, delimiter);
    const secondDataLine = dataLines.find((l, i) => l.trim() && l !== firstDataLine);
    const secondRow = secondDataLine ? parseCSVLine(secondDataLine, delimiter) : undefined;
    const layout = detectTimberCsvColumnLayout(firstRow, delimiter, secondRow);

    if (layout.hasLineNumberCol) {
        return csvText;
    }

    const enrichedData: string[] = [];
    let lineNumber = 0;

    for (const line of dataLines) {
        if (!line.trim()) {
            enrichedData.push(line);
            continue;
        }
        lineNumber += 1;
        const columns = parseCSVLine(line, delimiter);
        enrichedData.push([String(lineNumber), ...columns].join(delimiter));
    }

    return [...metadata, ...enrichedData].join('\n');
}

/** Detect tab vs comma from a sample line or cell array. */
export function detectTimberCsvDelimiter(sample: string | string[]): string {
    const line = Array.isArray(sample) ? sample.join(',') : sample;
    const tabCount = (line.match(/\t/g) || []).length;
    const commaCount = (line.match(/,/g) || []).length;
    return tabCount > commaCount ? '\t' : ',';
}

const INCH_CSV_PLACEHOLDER = '___INCH___';
const FOOT_CSV_PLACEHOLDER = '___FOOT___';

function restoreTimberCsvDimensionMarks(cell: string): string {
    return cell
        .replace(new RegExp(INCH_CSV_PLACEHOLDER, 'g'), '"')
        .replace(new RegExp(FOOT_CSV_PLACEHOLDER, 'g'), "'");
}

// Parse a single CSV line, handling quoted values and timber inch/foot marks
function parseCSVLine(line: string, delimiter: string = ','): string[] {
    if (delimiter === '\t') {
        return line.split('\t').map((cell) => restoreTimberCsvDimensionMarks(cell.trim()));
    }

    const result: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
        const char = line[i]!;

        if (char === '"') {
            if (inQuotes) {
                if (line[i + 1] === '"') {
                    current += '"';
                    i += 1;
                } else {
                    inQuotes = false;
                }
            } else if (current.length > 0 && /\d$/.test(current)) {
                // Unquoted Cadwork inch mark (e.g. 6", 5 1/2") — not an Excel quoted field.
                current += '"';
            } else {
                inQuotes = true;
            }
            continue;
        }

        if (char === delimiter && !inQuotes) {
            result.push(restoreTimberCsvDimensionMarks(current.trim()));
            current = '';
            continue;
        }

        current += char;
    }

    result.push(restoreTimberCsvDimensionMarks(current.trim()));
    return result;
}

/** First rows of the timber CSV after the 3-line metadata header (for RFQ publish preview). */
export function buildProposalCsvPreviewTable(
    csvText: string,
    maxRows = 50,
): { rows: string[][]; totalDataLines: number; delimiterLabel: string } {
    // LOD100: keep the embedded header row (needed by preview display detection)
    // and skip wide-row expansion, which assumes the classic column layout.
    const lod100Split = splitLod100Lines(csvText);
    if (lod100Split) {
        const { lines, headerLineIdx, headerCells, delimiter } = lod100Split;
        const map = lod100ColumnMapFromHeader(headerCells);
        const parsedRows: string[][] = [];
        let dataRowCount = 0;
        for (const line of lines.slice(headerLineIdx)) {
            if (!line.trim()) continue;
            const cells = parseCSVLine(line, delimiter);
            if (isLod100TrailerRow(cells, map)) continue;
            parsedRows.push(cells);
            if (!isLod100HeaderCells(cells) && !isLod100UnitsCells(cells)) dataRowCount += 1;
        }
        return {
            rows: parsedRows.slice(0, maxRows),
            totalDataLines: dataRowCount,
            delimiterLabel: delimiter === '\t' ? 'Tab' : 'Comma',
        };
    }

    const lines = csvText.trim().split(/\r?\n/);
    const dataLines = lines.slice(METADATA_ROW_COUNT);
    if (dataLines.length === 0) {
        return { rows: [], totalDataLines: 0, delimiterLabel: '—' };
    }
    const firstDataLine = dataLines.find((l) => l.trim()) || dataLines[0];
    const delimiter = detectTimberCsvDelimiter(firstDataLine);
    const firstRow = parseCSVLine(firstDataLine, delimiter);
    const secondDataLine = dataLines.find((l) => l.trim() && l !== firstDataLine);
    const secondRow = secondDataLine ? parseCSVLine(secondDataLine, delimiter) : undefined;
    const layout = detectTimberCsvColumnLayout(firstRow, delimiter, secondRow);

    const parsedRows: string[][] = [];
    let totalNonEmpty = 0;
    for (const line of dataLines) {
        if (!line.trim()) continue;
        totalNonEmpty += 1;
        parsedRows.push(parseCSVLine(line, delimiter));
    }

    const expandedRows = expandWideTimberCsvRows(parsedRows, layout);

    return {
        rows: expandedRows.slice(0, maxRows),
        totalDataLines: expandedRows.length,
        delimiterLabel: delimiter === '\t' ? 'Tab' : 'Comma',
    };
}

/** Parse all Cadwork timber data rows for generic CSV viewer (skips 3-line header). */
export function parseCadworkTimberCsvForDisplay(
    csvText: string,
    options?: TimberCsvParseOptions,
): {
    rows: string[][];
    columns: TimberCsvPreviewColumn[];
    delimiterLabel: string;
    totalDataLines: number;
} {
    const { rows, totalDataLines, delimiterLabel } = buildProposalCsvPreviewTable(
        csvText,
        Number.MAX_SAFE_INTEGER,
    );
    const { rows: displayRows, columns } = resolveTimberCsvPreviewDisplay(rows, options);
    return { rows: displayRows, columns, delimiterLabel, totalDataLines };
}

// Group parsed items by their group field
export function groupItemsByGroup(items: ParsedTimberItem[]): Record<string, ParsedTimberItem[]> {
    const groups: Record<string, ParsedTimberItem[]> = {};

    items.forEach((item) => {
        const groupName = item.group || 'Default';
        if (!groups[groupName]) {
            groups[groupName] = [];
        }
        groups[groupName].push(item);
    });

    return groups;
}

export interface ConvertToTimberListItemsOptions {
    sourceProjectFileId?: string;
    trueDimensions?: boolean;
}

// Convert parsed items to TimberListItem format for a structure
/** Convert parsed Cadwork CSV items into proposal node sets (one structure per CSV group). */
export function cadworkParsedItemsToNodeSets(
    items: ParsedTimberItem[],
    options?: ConvertToTimberListItemsOptions & { singleStructureName?: string },
): ProposalNodeSet[] {
    if (!items.length) return [];

    const buildNodeSet = (structureName: string, groupItems: ParsedTimberItem[], index: number) => {
        const timberList = convertToTimberListItems(groupItems, options);
        const ns = createDefaultNodeSet(structureName);
        ns.id = `nodeset-csv-${Date.now()}-${index}`;
        ns.materials.timberList = timberList;
        ns.materials.totalBoardFeet = timberList.reduce((sum, item) => sum + item.boardFeet * item.quantity, 0);
        ns.production = {
            ...ns.production,
            boardFeet: Math.round(ns.materials.totalBoardFeet * 100) / 100,
        };
        return ns;
    };

    if (options?.singleStructureName) {
        return [buildNodeSet(options.singleStructureName, items, 0)];
    }

    const grouped = groupItemsByGroup(items);
    return Object.entries(grouped).map(([groupName, groupItems], index) =>
        buildNodeSet(groupName, groupItems, index),
    );
}

export function convertToTimberListItems(
    items: ParsedTimberItem[],
    options?: ConvertToTimberListItemsOptions,
): TimberListItem[] {
    return items.map((item, index) => {
        const { species, grade, moistureContent } = parseTimberMaterialColumn(item.material, {
            speciesFallback: DEFAULT_PROPOSAL_TIMBER_SPECIES,
            gradeFallback: DEFAULT_PROPOSAL_TIMBER_GRADE,
        });
        return {
            id: `timber-${Date.now()}-${index}`,
            name: item.pieceName,
            species,
            grade,
            dimensions: item.normalizedDimensions,
            moistureContent,
            quantity: item.quantity,
            boardFeet: item.boardFeet,
            unitCost: 0,
            total: 0,
            sourceLineNumber: item.lineNumber,
            sourceProductionNumber: item.productionNumber,
            sourceProjectFileId: options?.sourceProjectFileId,
            ...(item.trueDimensions || options?.trueDimensions ? { trueDimensions: true } : {}),
        };
    });
}
